import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Search } from "lucide-react";

type RecentPost = { slug: string; title: string; published_at: string | null };

export function NotFound() {
  const [recent, setRecent] = useState<RecentPost[]>([]);
  const [q, setQ] = useState("");

  // Tell crawlers not to index 404s (cannot use route head() since this renders
  // for any unmatched URL via the root notFoundComponent).
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, follow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Page not found | Scott Cunningham";
    return () => {
      meta.remove();
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Dynamic import keeps the Supabase auth/storage bundles out of the
    // main chunk shared by every route. The 404 pays the cost only when hit.
    import("@/integrations/supabase/client").then(({ supabase }) => {
      if (cancelled) return;
      supabase
        .from("posts")
        .select("slug, title, published_at")
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(5)
        .then(({ data }) => {
          if (!cancelled) setRecent((data as RecentPost[]) ?? []);
        });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = q.trim();
    if (!v) {
      window.location.href = "/blog";
      return;
    }
    window.location.href = `/blog?q=${encodeURIComponent(v)}`;
  };

  return (
    <div className="relative overflow-hidden">
      <div
        className="blob float-slow -left-32 -top-24 h-[320px] w-[320px] bg-[var(--color-primary)]"
        style={{ opacity: 0.18 }}
      />
      <div
        className="blob float-slow right-[-120px] top-32 h-[300px] w-[300px] bg-[var(--color-electric)]"
        style={{ animationDelay: "-3s", opacity: 0.18 }}
      />
      <div
        className="blob float-slow bottom-[-160px] left-1/3 h-[340px] w-[340px] bg-[var(--color-violet)]"
        style={{ animationDelay: "-6s", opacity: 0.18 }}
      />
      <div className="relative mx-auto flex min-h-[70vh] max-w-2xl flex-col items-start justify-center px-6 py-24">
        <span className="inline-flex items-center gap-2 rounded-full border border-foreground/15 bg-background/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/80 backdrop-blur-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-primary)]" />
          404
        </span>
        <h1 className="mt-5 text-[clamp(2.5rem,7vw,5rem)] leading-[0.95] tracking-tight text-foreground">
          Page not found
        </h1>
        <div
          aria-hidden="true"
          className="mt-4 h-1 w-24 rounded-full"
          style={{
            backgroundImage:
              "linear-gradient(90deg, oklch(0.66 0.26 28), oklch(0.6 0.28 305), oklch(0.6 0.26 265))",
          }}
        />
        <p className="mt-6 max-w-md text-base text-foreground/75">
          The page you're looking for doesn't exist or has been moved. Try searching the field
          notes:
        </p>

        <form onSubmit={handleSubmit} className="mt-6 w-full max-w-md">
          <label className="relative block">
            <span className="sr-only">Search posts</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search posts, tags, topics…"
              aria-label="Search posts"
              className="w-full rounded-full border border-foreground/20 bg-background/80 py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-foreground/55 backdrop-blur transition-colors focus:border-foreground/60 focus:outline-none"
            />
          </label>
        </form>

        {recent.length > 0 && (
          <div className="mt-10 w-full max-w-md">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/65">
              Recent posts
            </p>
            <ul className="mt-3 space-y-2">
              {recent.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="block border-b border-foreground/10 py-2 text-foreground/85 transition-colors hover:text-foreground"
                  >
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/"
            className="inline-flex items-center rounded-full bg-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-background transition-transform hover:-translate-y-0.5"
          >
            Go home
          </Link>
          <Link
            to="/blog"
            className="inline-flex items-center rounded-full border border-foreground/20 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/80 transition-colors hover:border-foreground hover:text-foreground"
          >
            Browse Field Notes
          </Link>
        </div>
      </div>
    </div>
  );
}
